import Swiper from 'swiper';
import { Autoplay, EffectFade } from 'swiper/modules';
// css импортировать не нужно, так как он грузится бандлом

// Предполагаем, что Swiper уже подключен на странице

declare global {
    interface Window {
        prepareSlider?: any;
    }
}

class PrepareSlider {
    private swiper: Swiper | null;
    private swiperContainer: HTMLElement | null;
    private controlItems: NodeListOf<HTMLElement> | null;
    private slides: NodeListOf<HTMLElement> | null;
    private options: any;
    private resizeObserver: ResizeObserver | null;
    private isMobile: boolean;
    private firstSlideObserver: IntersectionObserver | null;
    private firstSlideAnimated: boolean;
    private autoplayTimeout: ReturnType<typeof setTimeout> | null;

    constructor(options) {
        this.swiper = null;
        this.swiperContainer = null;
        this.controlItems = null;
        this.slides = null;
        this.options = options;
        this.resizeObserver = null;
        this.isMobile = false;
        this.firstSlideObserver = null;
        this.firstSlideAnimated = false;
        this.autoplayTimeout = null;
        this.init();
    }

    init() {
        this.swiperContainer = document.querySelector(this.options.container);
        if (!this.swiperContainer) {
            return;
        }
        this.slides = this.swiperContainer.querySelectorAll('.prepare__content__slide');
        if (!this.slides || this.slides.length === 0) {
            return;
        }
        if (this.options.controlsContainer) {
            this.controlItems = document.querySelectorAll(`${this.options.controlsContainer} .prepare__controls__item`);
        }
        this.setupResizeObserver();
        this.checkScreenSize();
    }

    setupResizeObserver() {
        this.resizeObserver = new ResizeObserver(() => {
            this.checkScreenSize();
        });
        if (this.swiperContainer) {
            this.resizeObserver.observe(this.swiperContainer);
        }
    }

    checkScreenSize() {
        const currentIsMobile = window.innerWidth <= 767;
        if (this.isMobile !== currentIsMobile || !this.swiper) {
            this.isMobile = currentIsMobile;
            this.destroySwiper();
            this.initSwiper();
        }
    }

    destroySwiper() {
        if (this.swiper) {
            try {
                this.swiper.destroy(true, true);
            } catch (e) {
            }
            this.swiper = null;
        }
        this.destroyFirstSlideObserver();
        if (this.autoplayTimeout) {
            clearTimeout(this.autoplayTimeout);
            this.autoplayTimeout = null;
        }
    }

    setupFirstSlideObserver() {
        if (this.firstSlideObserver || !this.slides || this.slides.length === 0) return;
        const firstSlide = this.slides[0];
        if (!firstSlide) return;
        this.firstSlideObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting && !this.firstSlideAnimated) {
                    firstSlide.classList.add('animated');
                    this.firstSlideAnimated = true;
                    this.autoplayTimeout = setTimeout(() => {
                        if (this.swiper && this.slides && this.slides.length > 1) {
                            this.swiper.slideTo(1);
                        }
                    }, 6000);
                    this.destroyFirstSlideObserver();
                }
            });
        }, { threshold: 0.7 });
        this.firstSlideObserver.observe(firstSlide);
    }
    
    destroyFirstSlideObserver() {
        if (this.firstSlideObserver) {
            this.firstSlideObserver.disconnect();
            this.firstSlideObserver = null;
        }
    }

    initSwiper() {
        if (!this.swiperContainer) {
            return;
        }
        this.slides && this.slides.forEach(slide => {
            slide.classList.remove('active', 'animated');
        });
        this.firstSlideAnimated = false;
        try {
            this.swiper = new Swiper(this.swiperContainer as HTMLElement, {
                modules: [Autoplay, EffectFade],
                direction: 'horizontal',
                slidesPerView: 1,
                spaceBetween: 30,
                effect: 'fade',
                fadeEffect: {
                    crossFade: true
                },
                speed: 800,
                loop: false,
                autoplay: {
                    delay: 7000,
                    disableOnInteraction: false
                },
                observer: true,
                observeParents: true,
                on: {
                    init: () => {
                    },
                    slideChange: () => {
                        this.handleSlideChange();
                    },
                    slideChangeTransitionStart: () => {
                        this.slides && this.slides.forEach((slide, index) => {
                            if (index !== (this.swiper ? this.swiper.activeIndex : -1)) {
                                slide.classList.remove('animated');
                            }
                        });
                    },
                    slideChangeTransitionEnd: () => {
                        if (this.swiper) {
                            const activeSlide = this.slides && this.slides[this.swiper.activeIndex];
                            if (activeSlide) {
                                if (this.swiper.activeIndex !== 0 || this.firstSlideAnimated) {
                                    activeSlide.classList.add('animated');
                                }
                            }
                        }
                    },
                    destroy: () => {
                    }
                }
            });
        } catch (e) {
        }
        if (this.slides && this.slides.length > 0) {
            const firstSlide = this.slides[0];
            firstSlide.classList.add('active');
            this.setupFirstSlideObserver();
        }
        this.setupControlsEvents();
    }

    handleSlideChange() {
        if (!this.swiper || !this.slides) return;
        this.slides.forEach((slide, index) => {
            if (index === this.swiper!.activeIndex) {
                slide.classList.add('active');
            } else {
                slide.classList.remove('active');
            }
        });
        if (this.controlItems) {
            this.controlItems.forEach((control, index) => {
                if (index === this.swiper!.activeIndex) {
                    control.classList.add('active');
                } else {
                    control.classList.remove('active');
                }
            });
        }
    }

    setupControlsEvents() {
        if (!this.controlItems || !this.swiper) return;
        this.controlItems.forEach((control, index) => {
            control.addEventListener('click', () => {
                this.swiper && this.swiper.slideTo(index);
            });
            if (index === 0) {
                control.classList.add('active');
            }
        });
    }

    destroy() {
        this.destroySwiper();
        this.destroyFirstSlideObserver();
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
    }
}

// Автоматическая инициализация, если элемент .prepare__content__wrapper существует

document.addEventListener('DOMContentLoaded', function() {
    // Remove notInit class to enable animations while keeping content SEO-friendly
    const prepareSection = document.getElementById('prepare');
    if (prepareSection) {
        prepareSection.classList.remove('notInit');
    }
   
    var prepareContainer = document.querySelector('.prepare__content__wrapper');
    if (prepareContainer) {
        
        var slider = new PrepareSlider({
            container: '.prepare__content__wrapper',
            controlsContainer: '.prepare__controls'
        });
        window.prepareSlider = slider;
    } else {
        
    }
});
